/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePreviousDistinct } from 'react-use'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { SwapPairsDialog } from '@root/features/wallet/wallet-swap/ui/desktop/swap-pairs-dialog'
import { CurrencySelectorDialog } from '@root/widgets/currency-selector/ui/desktop'

import { reatomWalletSwap } from '../../atoms'
import { WalletSwapDetails } from '../common/wallet-swap-details/wallet-swap-details'
import { WalletSwapInput } from '../common/wallet-swap-input'

import { WalletSwapButton } from './wallet-swap-button'

import type { WalletSwapModel } from '../../atoms'
import type { WalletModel } from '@root/entities/wallet'
import type { CurrencySet } from '@root/widgets/currency-selector/atoms'

const CURRENCY_SETS: Array<CurrencySet> = ['wallet', 'default', 'trending']

type WalletSwapProps = {
  wallet: WalletModel
  model?: WalletSwapModel
}

export const WalletSwap: React.FC<WalletSwapProps> = ({
  wallet,
  model: model_,
}) => {
  const { t } = useTranslation()
  const model = useMemo(
    () => model_ ?? reatomWalletSwap(wallet),
    [wallet, model_],
  )
  useAtom(model.triggerAtom)

  const [baseSelectorOpen, setBaseSelectorOpen] = useState(false)
  const [quoteSelectorOpen, setQuoteSelectorOpen] = useState(false)
  const [swapPairsDialogOpen, setSwapPairsDialogOpen] = useState(false)

  const [inCurrency] = useAtom(model.input.inCurrencyAtom)
  const [outCurrency] = useAtom(model.input.outCurrencyAtom)
  const [quoteState] = useAtom(model.quote.quoteStateAtom)
  const [settings] = useAtom(model.settings.swapSettingsAtom)
  const [pairsState] = useAtom(model.pair.poolsStateAtom)
  const pairStatePrevious = usePreviousDistinct(pairsState)

  const setBaseCurrency = useAction(model.input.setInCurrencyAction)
  const setQuoteCurrency = useAction(model.input.setOutCurrencyAction)
  const selectPair = useAction(model.pair.selectPoolAction)
  const retryQuote = useAction(model.quote.forceStartPolling)
  const openSettings = useAction(model.settings.openSettingsAction)

  useEffect(() => {
    if (pairsState.type === pairStatePrevious?.type) {
      return
    }
    if (!AsyncStateN.isSuccess(pairsState)) {
      return
    }
    if (pairsState.value.length > 1) {
      setSwapPairsDialogOpen(true)
    }
  }, [pairsState, pairStatePrevious])

  const pairs = pairsState.value ?? []

  return (
    <Root>
      <Content>
        <Inputs
          inputModel={model.input}
          quoteModel={model.swapQuoteModel}
          onChangeBaseCurrency={() => {
            setBaseSelectorOpen(true)
          }}
          onChangeQuoteCurrency={() => {
            setQuoteSelectorOpen(true)
          }}
        />
        {inCurrency &&
        outCurrency &&
        !AsyncStateN.isNotInitiated(quoteState) ? (
          <Details
            baseCurrency={inCurrency}
            quoteCurrency={outCurrency}
            quoteState={quoteState}
            selectedPool={undefined}
            settings={settings}
            onPairClick={() => {
              setSwapPairsDialogOpen(true)
            }}
            onOpenSettings={openSettings}
            onRetryQuote={retryQuote}
          />
        ) : undefined}
      </Content>
      <WalletSwapButton
        model={model}
        onSelectPair={() => {
          setSwapPairsDialogOpen(true)
        }}
      />

      <CurrencySelectorDialog
        open={baseSelectorOpen}
        onCurrencySelect={(currency) => {
          setBaseSelectorOpen(false)
          setBaseCurrency(currency)
        }}
        onOpenChange={setBaseSelectorOpen}
        title={t('swap.base-label')}
        wallet={wallet}
        sets={CURRENCY_SETS}
      />

      <CurrencySelectorDialog
        open={quoteSelectorOpen}
        onCurrencySelect={(currency) => {
          setQuoteSelectorOpen(false)
          setQuoteCurrency(currency)
        }}
        onOpenChange={setQuoteSelectorOpen}
        title={t('swap.quote-label')}
        wallet={wallet}
        sets={CURRENCY_SETS}
      />

      <SwapPairsDialog
        open={swapPairsDialogOpen}
        onOpenChange={setSwapPairsDialogOpen}
        onSelectPool={(pair) => {
          selectPair(pair)
          setSwapPairsDialogOpen(false)
        }}
        pairs={pairs}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  min-height: 0;
  align-items: start;
`

const Content = styled.div``
const Inputs = styled(WalletSwapInput)`
  --swap-input-bg: var(--backgroundA-2);
`
const Details = styled(WalletSwapDetails)`
  --swap-details-label-font-size: 12px;
  --swap-details-value-font-size: 12px;
  --swap-details-row-gap: 10px;
  margin-top: 12px;
  padding-bottom: 16px;
`
